import { motion } from "framer-motion";
import {
  Briefcase,
  Building,
  Calendar,
  CreditCard,
  FileText,
  Shield,
} from "lucide-react";
import React from "react";
import { UserRole } from "../../../types/user";

interface RoleSpecificFieldsProps {
  formData: any;
  selectedRole: UserRole;
  onInputChange: (field: string, value: any) => void;
  getRoleDisplayName: (role: UserRole) => string;
  getRoleCategory: (role: UserRole) => string;
}

const RoleSpecificFields: React.FC<RoleSpecificFieldsProps> = ({
  formData,
  selectedRole,
  onInputChange,
  getRoleDisplayName,
  getRoleCategory,
}) => {
  const category = getRoleCategory(selectedRole);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <h3 className="text-lg font-medium text-gray-900">
          {category.charAt(0).toUpperCase() + category.slice(1)} Specific
          Information
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Additional details for {getRoleDisplayName(selectedRole)}
        </p>
      </div>

      {category === "provider" && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Business Name
            </label>
            <div className="mt-1 relative">
              <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={formData.businessName || ""}
                onChange={(e) => onInputChange("businessName", e.target.value)}
                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Business Registration Number
            </label>
            <div className="mt-1 relative">
              <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={formData.businessRegNumber || ""}
                onChange={(e) =>
                  onInputChange("businessRegNumber", e.target.value)
                }
                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Tax Identification Number
            </label>
            <div className="mt-1 relative">
              <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={formData.taxId || ""}
                onChange={(e) => onInputChange("taxId", e.target.value)}
                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Business Type
            </label>
            <select
              value={formData.businessType || ""}
              onChange={(e) => onInputChange("businessType", e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              required
            >
              <option value="">Select Business Type</option>
              <option value="salon">Beauty Salon</option>
              <option value="spa">Spa</option>
              <option value="barbershop">Barbershop</option>
              <option value="wellness">Wellness Center</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
      )}

      {category === "service" && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Specialization
            </label>
            <div className="mt-1 relative">
              <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={formData.specialization || ""}
                onChange={(e) =>
                  onInputChange("specialization", e.target.value)
                }
                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="e.g., Hair cutting, Nail art, Massage therapy"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Years of Experience
            </label>
            <input
              type="number"
              value={formData.experience || ""}
              onChange={(e) => onInputChange("experience", e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              min="0"
              max="50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Certifications
            </label>
            <textarea
              value={formData.certifications || ""}
              onChange={(e) => onInputChange("certifications", e.target.value)}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="List relevant certifications and qualifications"
            />
          </div>
        </div>
      )}

      {category === "staff" && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Department
            </label>
            <select
              value={formData.department || ""}
              onChange={(e) => onInputChange("department", e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              required
            >
              <option value="">Select Department</option>
              <option value="support">Customer Support</option>
              <option value="delivery">Delivery & Logistics</option>
              <option value="management">Management</option>
              <option value="hr">Human Resources</option>
              <option value="finance">Finance</option>
              <option value="marketing">Marketing</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Employee ID
            </label>
            <input
              type="text"
              value={formData.employeeId || ""}
              onChange={(e) => onInputChange("employeeId", e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Start Date
            </label>
            <div className="mt-1 relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={formData.startDate || ""}
                onChange={(e) => onInputChange("startDate", e.target.value)}
                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                required
              />
            </div>
          </div>
        </div>
      )}

      {category === "customer" && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Date of Birth
            </label>
            <div className="mt-1 relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={formData.dateOfBirth || ""}
                onChange={(e) => onInputChange("dateOfBirth", e.target.value)}
                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Gender
            </label>
            <select
              value={formData.gender || ""}
              onChange={(e) => onInputChange("gender", e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
              <option value="prefer_not_to_say">Prefer not to say</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Preferred Services
            </label>
            <textarea
              value={formData.preferredServices || ""}
              onChange={(e) =>
                onInputChange("preferredServices", e.target.value)
              }
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="List preferred beauty services"
            />
          </div>
        </div>
      )}

      {category === "admin" && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Access Level
            </label>
            <select
              value={formData.accessLevel || ""}
              onChange={(e) => onInputChange("accessLevel", e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              required
            >
              <option value="">Select Access Level</option>
              <option value="full">Full System Access</option>
              <option value="limited">Limited Access</option>
              <option value="read_only">Read Only</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Security Clearance
            </label>
            <div className="mt-1 relative">
              <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={formData.securityClearance || ""}
                onChange={(e) =>
                  onInputChange("securityClearance", e.target.value)
                }
                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="Security clearance level"
              />
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default RoleSpecificFields;
